import { zodResolver } from '@hookform/resolvers/zod';
import { useSession } from '@hooks/security/use-session';
import { useAssessHeartAttack } from '@hooks/register/screening/use-assess-heart-attack';
import { useLocalize } from '@hooks/use-localize';
import { useStepForm } from '@hooks/use-step-form';
import { assessHeartAttackSchema } from '@views/register/screening/assess-heart-attack.schema';
import { asString, asStringBool } from '@/utils/parse';
import { DashRegistrationStep, screeningLinkMap } from '@/types/dashboard';
import type { IAssessHeartAttackFormValues, IAssessHeartAttackModel, IAssessHeartAttackSubmitResult } from '@/types/screening/assess-heart-attack';
import PageLoader from '@components/page-loader';
import StepFormLayout from '@components/step-form-layout';
import FieldError from '@components/field-error';

// Nkr.Model.Enums.MedicalAssessments.RemissionStatus (value → default English label).
const REMISSION_OPTIONS: { value: number; label: string }[] = [
    { value: 0, label: 'Less than 1 year' },
    { value: 1, label: '1 year' },
    { value: 2, label: '2 years' },
    { value: 3, label: '3 years' },
    { value: 4, label: '4 years' },
    { value: 5, label: '5 years' },
    { value: 6, label: '6 years' },
    { value: 7, label: '7 years' },
    { value: 8, label: '8 years' },
    { value: 9, label: '9 years' },
    { value: 10, label: '10 years' },
    { value: 11, label: '11 years' },
    { value: 12, label: '12 years' },
    { value: 13, label: '13 years' },
    { value: 14, label: '14 years' },
    { value: 15, label: '15+ years' },
];

const AssessHeartAttack = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError } = useAssessHeartAttack(registrationId, editKey);

    return (
        <PageLoader isLoading={isLoading} error={error}>
            <AssessHeartAttackContent
                model={model}
                isSubmitting={isSubmitting}
                submitError={submitError}
                onSubmit={submit}
            />
        </PageLoader>
    );
};

interface IAssessHeartAttackContentProps {
    model: IAssessHeartAttackModel;
    isSubmitting: boolean;
    submitError: string | null;
    onSubmit: (values: IAssessHeartAttackFormValues) => Promise<IAssessHeartAttackSubmitResult>;
}

const AssessHeartAttackContent = ({ model, isSubmitting, submitError, onSubmit }: IAssessHeartAttackContentProps) => {
    const t = useLocalize(model.languageData);

    const form = useStepForm<IAssessHeartAttackFormValues, IAssessHeartAttackSubmitResult>({
        resolver: zodResolver(assessHeartAttackSchema),
        defaultValues: {
            hasHadHeartAttack: asStringBool(model.hasHadHeartAttack),
            heartAttackRemissionStatus: asString(model.heartAttackRemissionStatus),
        },
        submit: onSubmit,
        linkMap: screeningLinkMap(model.isFemale, model.isLiver),
    });

    const {
        register,
        watch,
        formState: { errors },
    } = form;

    const showStatus = watch('hasHadHeartAttack') === '1';

    return (
        <StepFormLayout
            kind="screening"
            model={model}
            step={DashRegistrationStep.AssessHeartAttack}
            title={t('DashLabel_ProgressHeartAttack', 'Heart Attack')}
            t={t}
            form={form}
            isSubmitting={isSubmitting}
            submitError={submitError}
        >
            <div className="form-group">
                <label className="control-label col-xs-12">
                    {t('DashLabel_HaveYouEverHadAHeartAttack', 'Have you ever had a heart attack')}
                    ?<FieldError message={errors.hasHadHeartAttack?.message} />
                </label>
                <div className="col-xs-12" style={{ marginLeft: 10 }}>
                    <label className="radio-inline">
                        <input
                            type="radio"
                            value="1"
                            {...register('hasHadHeartAttack')}
                        />{' '}
                        {t('Word_Yes', 'Yes')}
                    </label>
                    <label className="radio-inline">
                        <input
                            type="radio"
                            value="0"
                            {...register('hasHadHeartAttack')}
                        />{' '}
                        {t('Word_No', 'No')}
                    </label>
                </div>
            </div>

            {showStatus && (
                <div className="sub-question" id="status">
                    <div className="form-group">
                        <label className="control-label col-xs-12">
                            {t(
                                'DashLabel_HowLongSinceYourHeartAttack',
                                'How long since your heart attack',
                            )}
                            ?
                            <FieldError
                                message={errors.heartAttackRemissionStatus?.message}
                            />
                        </label>
                        <div className="col-xs-12" style={{ marginLeft: 10 }}>
                            <select
                                className="form-control"
                                {...register('heartAttackRemissionStatus')}
                            >
                                <option value="">
                                    {t('DashLabel_PleaseSelect', 'Please Select')}
                                </option>
                                {REMISSION_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {t(
                                            `Enum_RemissionStatus_${option.value}`,
                                            option.label,
                                        )}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            )}
        </StepFormLayout>
    );
};

export default AssessHeartAttack;
